// src/components/PropertyField.tsx
// A single property row with label and value for the compact properties panel.

import type { ReactNode } from 'react';
import { cn } from '@core/ui';
import { useI18n } from '@react/hooks';

export interface PropertyFieldProps {
  /** i18n key for the label */
  label: string;
  /** Optional SVG path data for a leading icon */
  icon?: string;
  /** Value content rendered on the right */
  children: ReactNode;
  /** Click handler for inline editing */
  onClick?: () => void;
  /** Whether the value is editable (enables hover style) */
  editable?: boolean;
  /** Additional CSS classes for the root element */
  className?: string;
}

export function PropertyField({
  label,
  icon,
  children,
  onClick,
  editable = false,
  className,
}: PropertyFieldProps) {
  const { t } = useI18n();
  const translatedLabel = t(label);
  const isInteractive = editable && !!onClick;

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-0 py-1',
        className,
      )}
    >
      {/* Label */}
      <span className="flex items-center gap-1.5 w-24 shrink-0 text-sm text-muted-foreground">
        {icon && (
          <svg
            className="h-3.5 w-3.5 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d={icon} />
          </svg>
        )}
        {translatedLabel}
      </span>

      {/* Value */}
      {isInteractive ? (
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'flex-1 min-w-0 text-left text-sm text-foreground',
            'rounded px-1.5 py-0.5 -mx-1.5',
            'hover:bg-accent cursor-pointer',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'transition-colors',
          )}
          aria-label={translatedLabel}
        >
          {children}
        </button>
      ) : (
        <span className="flex-1 min-w-0 text-sm text-foreground">
          {children}
        </span>
      )}
    </div>
  );
}
